/**
 * Top bar for the Run view — app icon + title (left) and the project-specific
 * user menu (right). Renders the SHARED EndUserTopBar (the same component the
 * standalone runtime client uses), driven by the same per-(owner, project)
 * site-user store as the public preview. When no site session exists it
 * offers a compact sign-in (local project-users). All platform plumbing —
 * site identity, approvals polling + inbox, app switcher, sites button,
 * theme store, project settings — stays here and is injected as props.
 */
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  approvalList, livePreviewExecute, notificationPrefs, notificationsCount,
  notificationsList, notificationsMarkRead, saveNotificationPrefs,
  worklistComplete, worklistCreate, worklistList, worklistPrincipals,
  worklistSeed,
} from '@/livepreview/api'
import {
  AssignTaskDialog, NotificationBell, Worklist,
  type AppNotification, type NotificationPrefs, type TaskSeed, type WorkTask,
} from '@twasta/page-runtime/shell'
import { makeApprovalsRuntime } from '@twasta/page-runtime/txn/screen'
import ActivityReport from '@twasta/page-runtime/activity/ActivityReport'
import { activityAdapter } from '@/api/activity'
import {
  createTarget, documentTarget, recordTarget, supportIssueTarget,
} from '@twasta/page-runtime/search'
import { useLocation, useNavigate } from 'react-router-dom'
import ApprovalsInbox from '@/livepreview/runtime/ApprovalsInbox'
import { logout as platformLogout } from '@/api/auth'
import { makeDmsClient } from '@/api/dms'
import { DocumentDetail, personName } from '@twasta/page-runtime/dms'
import { APP_BASE } from '@/config/basePath'
import { useThemeStore } from '@/store/themeStore'
import {
  siteWhoami, siteRunIdentity,
  getSiteToken, setSiteToken, type SiteUser,
} from '@/api/website'
import ProjectAuthModal from './ProjectAuthModal'
import RunAppPicker, { canRunApp } from './RunAppPicker'
import { recordRun } from './runUsage'
import { useProjectStore } from '@/store/projectStore'
import { runAppIdentity } from '@/api/runapp'
import { endRunSession, suppressAdopt } from '@/api/runSession'
import {
  EndUserTopBar, makeSiteShellApi, siteIdentity, platformNotify,
  type ShellApi, type ShellCapabilities, type ShellIdentity, type AdminExtraTab,
} from '@/components/runtime-shell'
import { RuntimeAiPanel } from './RuntimeAiSettings'
import { AgentsPanel } from '@twasta/page-runtime/shell/playbooks'
import { answerPlaybookTask, makeAgentsRuntime } from '@/api/playbooks'
import { RuntimeAiUsagePanel } from './RuntimeAiUsage'
import { OrganizationDatabasePanel } from './OrganizationDatabase'
import { OrganizationUsersPanel } from './OrganizationUsers'
import { OrgBillingPanel } from './OrgBillingPanel'
import PaymentSettingsRenderer from '@/livepreview/ecommerce/PaymentSettingsRenderer'
import { getMyTenantDatabase, listMyTenantMembers } from '@/api/team'
import ProjectSettingsModal from '@/components/userMemory/ProjectSettingsModal'
import { SecurityContextChip } from '@twasta/page-runtime/userMemory'
import { listUserMemory, type SecurityContextState } from '@/api/userMemory'
import SitesButton, { type SiteGroup } from '@/components/common/SitesButton'
import type { SupportButtonProps } from '@twasta/page-runtime/support'
import { getAppEnv, updateAppEnv } from '@/api/appEnv'
import { platformMessagingTransport } from '@/api/messaging'
import toast from 'react-hot-toast'

import type { User } from '@/types'

// The Messaging console pulls in TipTap + the template composer — loaded only
// when an administrator opens its tab.
const MessagingAdminPanelLazy = lazy(() => import('@twasta/page-runtime/messaging')
  .then((m) => ({ default: m.MessagingAdminPanel })))

function RunActivityPanel({ project }: { project: string }) {
  const adapter = useMemo(() => activityAdapter('project', project), [project])
  return <ActivityReport adapter={adapter} realms={['app', 'site']} compact />
}

export default function RunTopBar({
  userId, project, platformUser, projectUser = false, singleApp = false, app, sites = [], onOpenSearch,
  support,
}: {
  userId: string
  project: string
  /** Logged-in Twasta user — the default acting identity for the in-app run. */
  platformUser?: User | null
  /** True when a PROJECT USER is running the application — no Twasta account,
   *  identity and roles from the tenant registry. Changes which identity
   *  endpoint answers and what signing out means; the bar itself is the same. */
  projectUser?: boolean
  /** This surface serves ONE application — the app switcher is hidden.
   *  True on a project's own domain, where the other applications are
   *  not merely uninteresting: their URLs do not exist on this host. */
  singleApp?: boolean
  app: {
    title?: string | null; icon?: string | null; theme_color?: string | null
    /** `application.collect_payments` — the project's Advanced → Payments
     *  switch. Only when it is on does the admin console get a Payments tab. */
    collect_payments?: boolean
  }
  /** External Website (X) nodes for this app — surfaced as a "Site" button. */
  sites?: SiteGroup[]
  /** Opens application-wide search. Owned by EndUserAppView, which is what
   *  knows how to NAVIGATE to a result; absent → the button is hidden. */
  onOpenSearch?: () => void
  /** The Support module's button (bound by EndUserAppView, which owns the
   *  support identity and the navigation to the board); absent → hidden.
   *  (No "Updates" admin tab here: the platform is development/test, and live
   *  servers upgrade themselves from the release's git tag.) */
  support?: SupportButtonProps
}) {
  // One open per mount of this app — what the picker's usage sorts rank by.
  useEffect(() => { recordRun(project) }, [project])
  const [siteUser, setSiteUser] = useState<SiteUser | null>(null)
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'anonymous'>('loading')
  const [signInOpen, setSignInOpen] = useState(false)
  const [projectSettingsOpen, setProjectSettingsOpen] = useState(false)
  // Global "Approvals" entry — always reachable from the app header (not just
  // when a transaction list is open). Shows how many records are pending the
  // signed-in user's approval across EVERY screen. It is a fallback for the
  // badge and nothing else now: the inbox itself lives in the work panel's
  // Approvals tab, so there is no second surface to open from here.
  const [approvalsCount, setApprovalsCount] = useState<number | null>(null)
  // The unified worklist. Approvals are a FILTER inside it — to the person
  // doing the work, approving an order and receiving goods against one are the
  // same kind of obligation, so they belong in one list with one badge.
  const [worklistOpen, setWorklistOpen] = useState(false)
  const [workCount, setWorkCount] = useState<number | null>(null)
  // Assigning a record to somebody. Opened from a row's "Assign to…" (the
  // screen hands up which record and what it is called) and from the work
  // panel's own "New task"; ONE dialog either way, because they create the
  // same kind of task.
  const [assignFor, setAssignFor] = useState<{
    objName: string; recordKey: string; label?: string; objTitle?: string
  } | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  // Light/dark theme toggle, surfaced in the run user menu.
  const theme = useThemeStore((s) => s.theme)
  const toggleTheme = useThemeStore((s) => s.toggleTheme)
  // Opens the projects app-switcher (mounted by RunPage) so the user can jump
  // to another application — in Run, Workbench or Builder mode per their rights.
  // The Run view has its OWN landing-style picker; the builder's ProjectsModal
  // (left rail / Builder hub / Workbench) is untouched.
  const [appPickerOpen, setAppPickerOpen] = useState(false)
  // How many OTHER applications this caller can open. The Apps button only
  // earns its place when there is somewhere to go: a caller with a single
  // application (or a project user, who holds no platform account and so has
  // no list at all) is not shown a switcher that would open onto themselves.
  const fetchProjectList = useProjectStore((s) => s.fetchProjects)
  const projectList = useProjectStore((s) => s.projects)
  const [appsLoaded, setAppsLoaded] = useState(false)
  useEffect(() => {
    if (singleApp || projectUser) return undefined
    let cancelled = false
    Promise.resolve(fetchProjectList())
      .catch(() => { /* no list → no switcher */ })
      .finally(() => { if (!cancelled) setAppsLoaded(true) })
    return () => { cancelled = true }
  }, [singleApp, projectUser, fetchProjectList])
  const otherApps = projectList.filter(
    (p) => canRunApp(p) && p.application.folder_name !== project,
  ).length
  // The Twasta caller's runtime identity for this project: the project owner the
  // app's user store lives under, the functional roles the caller holds, and
  // whether those include FUNC-ADMIN (→ runtime "System administration").
  const [ownerId, setOwnerId] = useState(userId)
  const [runRoles, setRunRoles] = useState<string[]>([])
  const [runIsAdmin, setRunIsAdmin] = useState(false)
  // Context-based security: the entity this person's rights follow (e.g.
  // their financial entity), shown in the bar with one click to switch.
  const [securityContext, setSecurityContext] = useState<SecurityContextState | null>(null)
  const [securityContextLabel, setSecurityContextLabel] = useState<string | null>(null)
  // Whether this caller may manage the database THEIR organization runs the
  // app in — true only for a func-admin outside the owner's organization. The
  // endpoint is the authority (it 400s/403s otherwise), so asking it is also
  // how the tab decides to exist.
  const [canChangeDb, setCanChangeDb] = useState(false)
  const [canManageOrgUsers, setCanManageOrgUsers] = useState(false)
  // Opens the Support panel on an issue (a reporter's "answer the question"
  // task, or a notification about their issue).
  const [supportRequest, setSupportRequest] = useState<{ issue: number; nonce: number } | null>(null)
  // `openTask` is created once; it reaches the current `openSupport` (which
  // depends on the resolved support identity) through this ref.
  const openSupportRef = useRef<(project: string, number?: unknown, tab?: string) => void>(() => {})

  // Resolve the Twasta caller's runtime identity (cookie auth; independent of
  // any site-user session). FUNC-ADMIN holders get admin; the resolved owner
  // keys the app's user store so this works for teammates, not just the owner.
  useEffect(() => {
    if (!userId || !project) return
    let cancelled = false
    // A project user's runtime identity comes from the registry session they
    // signed in with, not from a platform cookie they do not have. Same answer
    // shape either way — including FUNC-ADMIN, so a project user made an
    // administrator of the application gets its System administration panel.
    const identity = projectUser
      ? runAppIdentity(project)
      : siteRunIdentity(userId, project)
    identity
      .then((r) => {
        if (cancelled) return
        setOwnerId(r.data.owner_user_id || userId)
        // Now that the application's real owner is known, name it on every
        // request (`api/appEnv`) — the bound project is this one.
        if (r.data.owner_user_id && getAppEnv()?.project === project) {
          updateAppEnv({ owner: r.data.owner_user_id })
        }
        setRunRoles(r.data.roles || [])
        setRunIsAdmin(!!r.data.is_admin)
        setSecurityContext(r.data.security_context || null)
      })
      .catch(() => { /* keep defaults — the user is still shown via platformUser */ })
    return () => { cancelled = true }
  }, [userId, project, projectUser])

  // The option LABEL of the entity signed into ("Entity One" rather than its
  // code) — read from the same list the Project settings dialog shows.
  useEffect(() => {
    const sc = securityContext
    if (!project || !sc?.value) { setSecurityContextLabel(null); return }
    let cancelled = false
    listUserMemory(project, 'internal')
      .then((vars) => {
        if (cancelled) return
        const v = vars.find((x) => x.name === sc.var)
        const opt = (v?.options || []).find((o) => String(o.value) === String(sc.value))
        setSecurityContextLabel(opt?.label || null)
      })
      .catch(() => { /* the code alone is still shown */ })
    return () => { cancelled = true }
  }, [project, securityContext])

  // Offer the Database tab only where it means something: a customer
  // organization running someone else's app. For the owner's own organization
  // the call answers 400 (that database is the project's, changed in Database
  // Designer) and the tab stays away.
  useEffect(() => {
    if (!project || !runIsAdmin) { setCanChangeDb(false); return }
    let cancelled = false
    getMyTenantDatabase(project)
      .then(() => { if (!cancelled) setCanChangeDb(true) })
      .catch(() => { if (!cancelled) setCanChangeDb(false) })
    return () => { cancelled = true }
  }, [project, runIsAdmin])

  // Same question for the Organization tab, asked separately: managing your
  // own organization's users is a wider scope than moving its database (the
  // owner's own func-admin has it too), so answering it with the database
  // probe would hide the tab from people entitled to it.
  useEffect(() => {
    if (!project || !runIsAdmin) { setCanManageOrgUsers(false); return }
    let cancelled = false
    listMyTenantMembers(project)
      .then(() => { if (!cancelled) setCanManageOrgUsers(true) })
      .catch(() => { if (!cancelled) setCanManageOrgUsers(false) })
    return () => { cancelled = true }
  }, [project, runIsAdmin])

  // Hydrate an explicit site-user session, keyed by the resolved project owner
  // (separate from the Twasta platform login that gates the /run route itself).
  useEffect(() => {
    if (!ownerId || !project) return
    let cancelled = false
    if (!getSiteToken(ownerId, project)) { setStatus('anonymous'); return }
    siteWhoami(ownerId, project)
      .then((r) => { if (!cancelled) { setSiteUser(r.data); setStatus('authenticated') } })
      .catch(() => { if (!cancelled) { setSiteToken(ownerId, project, null); setSiteUser(null); setStatus('anonymous') } })
    return () => { cancelled = true }
  }, [ownerId, project])

  // Pending-approval count for the header badge. Best-effort: a project with
  // no approval workflows simply returns 0, and any error leaves the badge
  // hidden rather than surfacing noise in the app chrome.
  const refreshApprovalsCount = useCallback(() => {
    if (!project) return
    // Only need the number: fetch a single row and read the pre-slice `total`
    // (falling back to `count` on older servers) — never pull the full staged
    // list just for a badge.
    approvalList(project, { limit: 1 })
      .then((res) => setApprovalsCount(res?.total ?? res?.count ?? res?.items?.length ?? 0))
      .catch(() => { /* keep the prior count / hidden badge */ })
  }, [project])

  useEffect(() => { refreshApprovalsCount() }, [refreshApprovalsCount])

  const refreshWorkCount = useCallback(() => {
    if (!project) return
    worklistList(project, { limit: 1 })
      .then((res) => setWorkCount(res?.total ?? 0))
      .catch(() => { /* keep the prior count; an empty badge would read as "nothing to do" */ })
  }, [project])

  useEffect(() => { refreshWorkCount() }, [refreshWorkCount])

  // The approvals family, bound the same way the inbox binds it — one
  // decision path, so approving from a worklist row and approving inside the
  // inbox run the identical action.
  const approvals = useMemo(() => makeApprovalsRuntime(
    (objName: string, action: string, payload: Record<string, unknown>) =>
      livePreviewExecute(project, objName, action, payload),
  ), [project])

  /** The injected I/O the shared panel calls. */
  const worklistRuntime = useMemo(() => ({
    list: (opts?: {
      status?: string; kinds?: string[]; limit?: number; offset?: number; all?: boolean
    }) => worklistList(project, opts),
    count: () => worklistList(project, { limit: 1 }).then((r) => ({
      total: r.total, by_kind: r.by_kind, overdue: r.overdue,
    })),
    seed: (taskKey: string) => worklistSeed(project, taskKey),
    complete: (taskKey: string) => worklistComplete(project, taskKey),
    create: (input: Record<string, unknown>) => worklistCreate(project, input),
    // Who work can be handed to. Its presence is what turns the panel's "New
    // task" button on — a picker with nobody in it is worse than no button.
    principals: (q?: string) => worklistPrincipals(project, q),
    // Approve straight from the row. Anything the row cannot carry — failed
    // validation, warnings to acknowledge, a document decision — comes back
    // as `ok:false` and the panel opens the record in the inbox instead.
    approve: async (task: WorkTask) => {
      const instanceId = String(task.target?.instance_id || '')
      // A DOCUMENT approval is decided by its repository, not by the generic
      // approval action — promotion moves a version pointer, and only the DMS
      // knows how. Same split the inbox makes, same call it makes.
      if (task.target?.dms) {
        const repo = String(task.target?.repo || '')
        const client = repo ? makeDmsClient(project, repo) : null
        if (!client) {
          return { ok: false, reason: 'This document is decided in its repository.' }
        }
        // No instance yet: this is a document sitting in a state it can only
        // leave through a sign-off, so the button RAISES that sign-off — the
        // same lifecycle action the repository's own Approve button runs. It
        // does not decide it: a controlled document is signed off explicitly,
        // and the row comes straight back as the sign-off to make.
        if (!instanceId) {
          const action = String(task.target?.action || 'approve')
          const documentId = String(task.target?.document_id || task.record_key || '')
          if (!documentId) return { ok: false, reason: 'That document could not be identified.' }
          const res = await client.act(documentId, action)
          if (res?.staged) {
            // Two steps, by the repository's design: this one RAISED the
            // sign-off, and the sign-off itself is the next row. Saying so is
            // the difference between a deliberate second step and a button
            // that looks like it did not work the first time.
            return {
              ok: true,
              next_record: documentId,
              message: 'Sent for sign-off. A controlled document is released in '
                + 'two steps — this raised the approval, and it is now waiting '
                + 'for your decision on the highlighted row below. Approve it '
                + 'there to release the document.',
            }
          }
          return { ok: true, message: `Done — the document is now ${res?.target_state || 'moved on'}.` }
        }
        if (!client.decideApproval) {
          return { ok: false, reason: 'This document is decided in its repository.' }
        }
        const dres = await client.decideApproval({ instanceId, decision: 'approve' })
        const status = String(dres?.status || '')
        if (status === 'approved') {
          return { ok: true, message: 'Approved — the document has been released.' }
        }
        // 'pending' means this level cleared and the next approver now holds
        // it — the work left this queue, which is what the row cares about.
        if (status === 'pending') {
          return { ok: true, message: 'Signed off — it is now with the next approver.' }
        }
        return { ok: false, reason: dres?.message || 'That document needs a closer look.' }
      }
      if (!instanceId) return { ok: false, reason: 'This one has to be opened to decide.' }
      const res = await approvals.approve(instanceId, {
        objName: task.obj_name || undefined,
      }) as { ok?: boolean; message?: string; warnings?: unknown[] }
      if (res?.ok === false || (Array.isArray(res?.warnings) && res.warnings.length > 0)) {
        return { ok: false, reason: res?.message || 'This approval needs a closer look.' }
      }
      return { ok: true }
    },
    // Name the documents behind any document rows — one `pendingApprovals()`
    // per repository, the same resolution the approvals inbox does.
    documents: async (repos: string[]) => {
      const merged: Record<string, {
        title?: string; target_state?: string
        pending_roles?: string; submitted_by?: string
      }> = {}
      await Promise.all(repos.map(async (repo) => {
        try {
          const client = makeDmsClient(project, repo)
          if (!client.pendingApprovals) return
          for (const row of await client.pendingApprovals()) {
            if (row?.instance_id) merged[String(row.instance_id)] = row
          }
        } catch {
          // A repository the caller may not list is not a worklist failure.
        }
      }))
      return merged
    },
    // Name whoever asked for the work. The directory a repository serves is
    // PROJECT-wide (it resolves the platform users and the tenant's own
    // project-user registry, which is exactly what the task index cannot
    // reach), so the first repository that answers names everybody.
    people: async (repos: string[]) => {
      const found: Record<string, string> = {}
      for (const repo of repos) {
        try {
          const client = makeDmsClient(project, repo)
          if (!client.listUsers) continue
          const list = await client.listUsers()
          for (const person of list) {
            const name = personName(list, person.user_key)
            if (name) found[person.user_key] = name
          }
          if (Object.keys(found).length > 0) break
        } catch {
          // A repository the caller may not read is not a worklist failure.
        }
      }
      return found
    },
    notify: (message: string, kind?: 'success' | 'error' | 'info') => {
      if (kind === 'error') platformNotify.error(message)
      else platformNotify.success(message)
    },
  }), [project, approvals])

  /** The bell's I/O — the same injected-runtime shape the work panel uses. */
  const notificationRuntime = useMemo(() => ({
    list: (opts?: { unreadOnly?: boolean; limit?: number }) =>
      notificationsList(project, opts || {}),
    count: () => notificationsCount(project),
    markRead: (ids?: string[]) => notificationsMarkRead(project, ids),
    prefs: () => notificationPrefs(project),
    savePrefs: (prefs: NotificationPrefs) => saveNotificationPrefs(project, prefs),
  }), [project])

  /**
   * Acting on a task. The panel deliberately does not navigate — that differs
   * per host — so the decision of WHERE a verb leads lives here, next to the
   * router this surface already owns.
   */
  const openTask = useCallback((
    task: WorkTask, seed?: TaskSeed, opts?: { scope?: 'mine' | 'all' },
  ) => {
    const target = task.target || {}
    const kind = target.open || (task.record_key ? 'record' : '')
    // Support work — a plan to approve, a fix to QC, a question for the
    // reporter — opens the issue itself.
    if (kind === 'support_issue') {
      setWorklistOpen(false)
      openSupportRef.current(String(target.project || project), target.number)
      return
    }
    // Approvals and document reviews never reach here: the panel shows them
    // in its own Approvals tab (renderApprovals below), which is where the
    // user already is. This handler is for work that leads somewhere ELSE.
    // A controlled document opens in its repository, on the document — the
    // same URL a search hit or an Assistant citation lands on.
    if (kind === 'document' || (target.dms && task.record_key)) {
      const t = documentTarget(String(target.obj_name || target.repo || task.obj_name || ''),
                               String(target.document_id || task.record_key || ''))
      if (t) { setWorklistOpen(false); goToTarget(t); return }
    }
    // A suspended / waiting Transaction Wizard run: its wizard's screen,
    // resuming that run (the wizard's own `?resume=` deep link).
    if (kind === 'wizard_resume' && target.run_id) {
      const wizard = String(target.wizard || target.obj_name || task.obj_name || '')
      if (wizard) {
        setWorklistOpen(false)
        goToTarget({ objName: wizard, params: { resume: String(target.run_id) } })
      }
      return
    }
    if (kind === 'url' && typeof target.url === 'string' && target.url) {
      window.open(target.url, '_blank', 'noopener')
      return
    }
    if (kind === 'create' && target.obj_name) {
      // The seed rides as a KEY; the screen fetches the payload behind it.
      const t = createTarget(String(target.obj_name), {
        formNo: typeof target.form_no === 'number' ? target.form_no : undefined,
        seedRef: seed?.task_key || task.task_key,
      })
      if (t) { setWorklistOpen(false); goToTarget(t) }
      return
    }
    if ((kind === 'record' || kind === 'action') && task.obj_name && task.record_key) {
      const t = recordTarget(String(target.obj_name || task.obj_name), task.record_key)
      if (t) { setWorklistOpen(false); goToTarget(t) }
      return
    }
    // Work that opens nothing (a call to make, a checklist) stays in the
    // panel: the row's own Done button is the whole interaction.
    platformNotify.success(
      task.verb ? `${task.verb}, then mark it done.` : 'Mark this done when it is finished.',
    )
  }, [])


  /**
   * Navigate to an OpenTarget inside the Run shell.
   *
   * The base is taken from the CURRENT path (`/run/:tenant/:project/...`)
   * rather than rebuilt from props: this bar knows the project but not the
   * tenant segment, and reconstructing it from the owner id would silently
   * send a cross-tenant viewer to the wrong app.
   */
  const goToTarget = useCallback((t: { objName: string; params: Record<string, string> }) => {
    const parts = location.pathname.split('/').filter(Boolean)
    const runIdx = parts.indexOf('run')
    if (runIdx < 0 || parts.length < runIdx + 3) return
    const base = `/${parts.slice(0, runIdx + 3).join('/')}/${encodeURIComponent(t.objName)}`
    const qs = new URLSearchParams(t.params).toString()
    navigate(qs ? `${base}?${qs}` : base)
  }, [navigate, location.pathname])

  /**
   * Open a Support issue (or one of the Support screens) from a task or a
   * notification. The support TEAM goes to the Run view's Support entry with
   * the issue open; a reporter, who has no board, gets the Support panel on
   * their issue. Another application's issue opens that application's Run
   * view — the same tenant, its own Support entry.
   */
  const openSupport = useCallback((targetProject: string, number?: unknown, tab?: string) => {
    const n = Number(number) || 0
    if (targetProject && targetProject !== project) {
      const parts = location.pathname.split('/').filter(Boolean)
      const runIdx = parts.indexOf('run')
      const tenant = runIdx >= 0 ? parts[runIdx + 1] : '_'
      const t = supportIssueTarget(n, tab)
      const qs = new URLSearchParams(t?.params || {}).toString()
      window.open(`${APP_BASE}/run/${tenant}/${encodeURIComponent(targetProject)}/${t?.objName}${qs ? `?${qs}` : ''}`,
        '_blank', 'noopener')
      return
    }
    if (support?.meta?.me.is_team) {
      const t = supportIssueTarget(n, tab)
      if (t) goToTarget(t)
      return
    }
    if (n) setSupportRequest({ issue: n, nonce: Date.now() })
  }, [project, location.pathname, support?.meta?.me.is_team, goToTarget])
  openSupportRef.current = openSupport

  const clearSession = useCallback(() => {
    setSiteToken(ownerId, project, null)
    setSiteUser(null)
    setStatus('anonymous')
    // For a project user this session IS how they are running the application
    // — dropping the token without dropping the run session would leave the
    // view sending headers for a session that no longer exists. Reloading
    // lands them back on the application's own sign-in.
    if (projectUser) {
      // Remember the choice before dropping the session: without it the Run
      // view would just open another one on reload (adopting needs no
      // password), and signing out would do nothing visible.
      suppressAdopt(project)
      endRunSession()
      window.location.reload()
    }
  }, [ownerId, project, projectUser])

  const onAuthed = useCallback((token: string, user: SiteUser) => {
    setSiteToken(ownerId, project, token)
    setSiteUser(user)
    setStatus('authenticated')
    setSignInOpen(false)
  }, [ownerId, project])

  const siteApi = useMemo(() => makeSiteShellApi(ownerId, project, clearSession), [ownerId, project, clearSession])

  // Identity for the in-app run defaults to the logged-in Twasta user — that's
  // the actual acting identity (data calls go through /api/livepreview with this
  // user's cookie, which is why the run has full access). Admin (the System
  // administration panel) is gated on the FUNC-ADMIN functional role. A separate
  // project site-user session, if one is explicitly signed into, takes
  // precedence so the owner can test the app as one of its end users.
  const platformIdentity: ShellIdentity | null = useMemo(() => {
    if (!platformUser) return null
    return {
      username: platformUser.user_name || platformUser.email || 'User',
      email: platformUser.email,
      // Runtime admin = holds FUNC-ADMIN on this project. Endpoints re-check
      // server-side, so this only drives whether the admin entry is offered.
      isAdmin: runIsAdmin,
      roles: runRoles,
    }
  }, [platformUser, runIsAdmin, runRoles])

  // When acting as the Twasta user (no site session), admin actions still
  // manage the project's site-users, but account/sign-out are platform-level:
  // password is managed in platform Settings, and sign-out ends the Twasta
  // session and returns to the workbench.
  const platformApi: ShellApi = useMemo(() => ({
    ...siteApi,
    changePassword: async () => {
      throw { response: { data: { detail: 'Manage your Twasta account password from the platform Settings.' } } }
    },
    logout: async () => {
      try { await platformLogout() } catch { /* idempotent */ }
      window.location.href = `${APP_BASE}/`
    },
  }), [siteApi])

  const usingSite = status === 'authenticated' && !!siteUser
  // Signing into the app as one of its own end users changes WHO you are acting
  // as — it does not take away what you are on the platform. The admin tabs
  // below are built from `runIsAdmin` (the FUNC-ADMIN functional role), so
  // gating the menu entry on the site user's own `admin` flag hid the console
  // from a func-admin the moment they signed in to test the app as an end user:
  // the tabs existed and nothing opened them. The server agrees with this —
  // `website._require_site_admin` accepts the FUNC-ADMIN cookie when the site
  // session is not itself an admin — so carry the platform answer across.
  const identity: ShellIdentity | null = useMemo(() => {
    const base = usingSite ? siteIdentity(siteUser) : platformIdentity
    if (!base || base.isAdmin || !runIsAdmin) return base
    return { ...base, isAdmin: true }
  }, [usingSite, siteUser, platformIdentity, runIsAdmin])
  const api = usingSite ? siteApi : platformApi
  const caps: ShellCapabilities = useMemo(
    () => ({ users: true, access: true, roles: true, dataAdminUrl: null }), [])

  // System administration → Agents: personas, playbook assignments and the
  // run monitor. Roles come from the same catalogue the Roles tab manages.
  const agentsRuntime = useMemo(() => makeAgentsRuntime(ownerId, project, {
    listRoles: async () => ((await api.listRoles?.()) || []).map((r) => r.name),
    notify: (text, tone) => (tone === 'error' ? platformNotify.error(text) : platformNotify.success(text)),
  }), [ownerId, project, api])
  // A playbook's Work List task is answered in the panel itself.
  const playbookTasks = useMemo(() => ({
    answer: async (sourceRef: string, answer: Record<string, unknown>) => {
      await answerPlaybookTask(ownerId, project, sourceRef, answer)
    },
  }), [ownerId, project])

  // The console's own Roles / Users / Access tabs manage the tenant-scoped
  // project users (Type-2) and their per-project rights — any runtime admin
  // (the owner/func-admin, or a cross-tenant func-admin holding runtime admin
  // on a project owned by another organization) works there. There used to be a
  // separate "Project users" tab beside them doing the same job under different
  // rules; now that the Users tab is invite-only too, it was a second answer to
  // one question, so it is gone.
  //
  // "AI" is the func-admin-owned tab that remains: the AI account answering the
  // end-user Assistant for THIS organization. A tenant running someone else's
  // app must set it up here — the builder's own AI profile never serves them.
  //
  // "Database" is the same idea for the data itself: which database this
  // organization's copy of the app runs in. It arrives on one the platform
  // provides, so this tab is where an organization CHOOSES to move it into a
  // database of its own — not a wall in front of the invitation.
  //
  // "Organization" is the people half of the same idea: which of the
  // administrator's OWN colleagues may use the application their organization
  // was granted. Without it the endpoints existed but nothing called them, so
  // every new colleague signed in to "No applications available" and waited
  // for someone with workbench access to add them by hand.
  const adminExtraTabs: AdminExtraTab[] | undefined = runIsAdmin
    ? [
      { id: 'ai', label: 'AI', render: () => <RuntimeAiPanel projectId={project} ownerId={ownerId} /> },
      // Its own tab rather than a section of "AI": configuring the assistant
      // and watching what it costs are done by the same person at very
      // different moments, and the report is the longer page of the two.
      { id: 'ai-usage', label: 'AI usage', render: () => <RuntimeAiUsagePanel projectId={project} ownerId={ownerId} /> },
      // Who uses this application and its website, and what they do — the
      // same report as the builder's Advanced → Activity, limited server-side
      // to app users and site visitors (the people BUILDING it are not the
      // administrator's to watch).
      { id: 'activity', label: 'Activity', render: () => <RunActivityPanel project={project} /> },
      // The application's AI personas and the agent playbooks they run —
      // assign a playbook with a run cycle and a credit budget, and watch,
      // pause, resume or cancel every run.
      { id: 'agents', label: 'Agents', render: () => <AgentsPanel runtime={agentsRuntime} /> },
      // Messaging — this organization's channels (connected through IOFlow),
      // its send buttons and automatic sends, its templates and the log.
      // Stored per tenant; the server re-checks func-admin on every call.
      {
        id: 'messaging', label: 'Messaging',
        render: () => (
          <Suspense fallback={null}>
            <MessagingAdminPanelLazy project={project} transport={platformMessagingTransport(project)}
              orgName={app.title || undefined} canConnect
              notify={{ success: (m: string) => toast.success(m), error: (m: string) => toast.error(m) }} />
          </Suspense>
        ),
      },
      // How THIS application takes its customers' money — the same form as the
      // builder's Advanced → Payments, written through the app-facing router.
      // It lives here rather than on a page in the application's navigation,
      // because it is running the application, not something its visitors
      // see. Offered only when the project says it collects payment (its
      // Advanced → Payments switch). The server still gates the calls (project
      // owner or FUNC-ADMIN); anyone else gets the renderer's "not yours" note.
      ...(app.collect_payments ? [{
        id: 'payments', label: 'Payments',
        render: () => (
          <PaymentSettingsRenderer project={project}
            block={{ id: 'admin-payments', type: 'payment_settings' }} />
        ),
      }] : []),
      ...(canManageOrgUsers ? [{
        id: 'organization', label: 'Organization',
        render: () => <OrganizationUsersPanel projectId={project} />,
      }, {
        id: 'org-billing', label: 'Billing',
        render: () => <OrgBillingPanel />,
      }] : []),
      ...(canChangeDb ? [{
        id: 'database', label: 'Database',
        render: () => <OrganizationDatabasePanel projectId={project} />,
      }] : []),
    ]
    : undefined

  return (
    <>
      <EndUserTopBar
        app={{ title: app.title || 'Application', icon: app.icon, themeColor: app.theme_color }}
        identity={identity}
        api={api}
        caps={caps}
        adminExtraTabs={adminExtraTabs}
        // One query across the records and documents this profile may see.
        onOpenSearch={onOpenSearch}
        // App switcher — jump to another application the user can access,
        // choosing Run / Workbench / Builder per their rights there.
        onOpenApps={singleApp || projectUser || !appsLoaded || otherApps === 0
          ? undefined
          : () => setAppPickerOpen(true)}
        appsCount={otherApps}
        // One button for everything waiting on this person. Approvals are a
        // filter inside the panel, not a second inbox — the split between
        // 'approve this' and 'do this' is ours, not the user's.
        // Report an issue / follow yours / (team) open the board.
        support={support ? { ...support, openRequest: supportRequest } : undefined}
        approvals={{
          count: (workCount ?? 0) || approvalsCount,
          onOpen: () => setWorklistOpen(true),
          label: 'Work List',
          title: 'Everything waiting on you — approvals, documents and tasks',
        }}
        // The app's site(s) open in a new tab from inside the running app —
        // and, beside them, the bell. A task is work you must do; a
        // notification is something that happened. Two badges, because one
        // that mixed them could never reach zero.
        sitesSlot={(
          <>
            {securityContext ? (
              <SecurityContextChip
                label={securityContext.label}
                value={securityContext.value}
                valueLabel={securityContextLabel}
                required={securityContext.required}
                onSwitch={() => setProjectSettingsOpen(true)}
              />
            ) : null}
            <NotificationBell
              runtime={notificationRuntime}
              onOpenLink={(link) => {
                // Almost everything worth telling somebody about is also
                // waiting for them: open the work panel rather than inventing
                // a second place to act.
                if (link?.open === 'record' && link.obj_name && link.record_key) {
                  const t = recordTarget(String(link.obj_name), String(link.record_key))
                  if (t) { goToTarget(t); return }
                }
                // A Support issue (status change, question, release of a fix)
                // or a release — open it, not the work panel.
                if (link?.kind === 'support_issue' || link?.open === 'support_issue') {
                  openSupport(String(link.project || project), link.number)
                  return
                }
                if (link?.kind === 'support_release') {
                  openSupport(String(link.project || project), undefined, 'releases')
                  return
                }
                setWorklistOpen(true)
              }}
            />
            <SitesButton sites={sites} userId={ownerId} project={project} />
          </>
        )}
        // Light/dark theme toggle + "Project settings" (the kind == "prompt"
        // project variables) — surfaced in the user menu.
        theme={{ value: theme, onToggle: toggleTheme }}
        onOpenProjectSettings={() => setProjectSettingsOpen(true)}
        // Only offer the project end-user sign-in when there's no identity to
        // show (no Twasta user and no site session) — normally the Twasta user
        // is shown by default.
        onSignIn={identity ? undefined : () => setSignInOpen(true)}
        notify={platformNotify}
      />
      {signInOpen && (
        <ProjectAuthModal userId={ownerId} project={project} onClose={() => setSignInOpen(false)} onAuthed={onAuthed} />
      )}
      {projectSettingsOpen && (
        <ProjectSettingsModal projectId={project} surface="internal" onClose={() => setProjectSettingsOpen(false)} />
      )}
      {assignFor && (
        <AssignTaskDialog
          runtime={worklistRuntime}
          record={assignFor}
          onClose={() => setAssignFor(null)}
          onCreated={() => { refreshWorkCount() }}
        />
      )}
      {worklistOpen && (
        <Worklist
          runtime={worklistRuntime}
          onClose={() => { setWorklistOpen(false); refreshWorkCount(); refreshApprovalsCount() }}
          onOpenTask={openTask}
          onCountChange={setWorkCount}
          playbookTasks={playbookTasks}
          // The Approvals tab IS the real inbox — the same panel the
          // designer's live preview shows — embedded in the worklist rather
          // than stacked over it, opened on the row the user clicked.
          // A document waiting for its sign-off opens IN the panel too — the
          // repository's own document surface (viewer, metadata, lifecycle
          // buttons), so acting on one does not cost the rest of the queue.
          renderDocument={({ repo, documentId }) => (
            <DocumentDetail
              client={makeDmsClient(project, repo)}
              documentId={documentId}
              onChanged={() => { refreshWorkCount() }}
              onDecided={() => { refreshWorkCount(); refreshApprovalsCount() }}
            />
          )}
          renderApprovals={({ instanceId, scope }) => (
            <ApprovalsInbox
              embedded
              project={project}
              initialShowAll={scope === 'all'}
              initialInstanceId={instanceId}
              onClose={() => { setWorklistOpen(false); refreshWorkCount() }}
              onCountChange={setApprovalsCount}
            />
          )}
        />
      )}
      {appPickerOpen && <RunAppPicker currentProject={project} onClose={() => setAppPickerOpen(false)} />}
    </>
  )
}
